import React, { useState } from "react";
import { fmtDateShort } from "../../utils/fmtDate";
import {
  toDateInputValue,
  toDateTimeLocalValue,
  toApiDateTime,
  defaultEndFromLaunch,
  todayDateInputValue,
} from "../../utils/scheduleDates";
import {
  updateCampaignSchedule,
  withdrawAndReschedule,
} from "../../utils/campaignViewActions";

/**
 * CampaignSchedulePanel — show, set and change a campaign's start and end date.
 * QA 2026-07-31, handoff §B.4.
 *
 * WHY IT IS A SHARED CARD AND NOT AN ACTION PANEL. Panel selection in
 * CampaignViewPage is ROUTE-based (getConfigByRoute -> config.viewType), and
 * ReadOnlyActions returns null for six statuses. A schedule inside the panels
 * would therefore be invisible on exactly the queues where a missing end date is
 * the diagnosis. CoOwnershipPanel is the precedent: a status-independent sibling
 * by design.
 *
 * Editability is derived from the ROW's campaign_status, not from the route: the
 * route only says which queue the admin arrived from, and would offer a control
 * on a campaign whose status has since moved.
 *
 * IT IS DELIBERATELY NOT PASSED `setLoading`. Every write here goes through
 * confirmAction, and F-1 (admin 520c3c0) was that the page-level .loader-overlay
 * at z-index 99999 sits above the SweetAlert container and swallows every click.
 * Not accepting the prop makes that structural rather than a comment someone can
 * delete later.
 *
 * WHY "Not set" IS LOUD. The owner decided against a backfill, so an absent end
 * date is a call to action. It is styled as a warning rather than in the muted
 * grey the other labels use, because grey reads as "nothing here" — which is
 * precisely how the never-closes defect stayed hidden.
 */

const APPROVED = 3;
const COMING_SOON = 9;
const HOLD = 8;

// Statuses whose schedule can still be changed. Everything else is terminal or
// pre-decision, and the API refuses it — so offer an explanation, not a control.
const EDITABLE_STATUSES = [APPROVED, COMING_SOON, HOLD];

function whyReadOnly(status) {
  switch (Number(status)) {
    case 1:
      return "A campaign's schedule is set when it is approved.";
    case 6:
      return "This campaign is still a draft.";
    case 2:
      return "This campaign was rejected.";
    case 4:
      return "This campaign has completed.";
    case 7:
      return "This campaign is closed.";
    case 5:
      return "This campaign is deleted.";
    case 10:
      return "This campaign was cancelled.";
    default:
      return "This campaign's schedule cannot be changed in its current state.";
  }
}

function Value({ stored, testId }) {
  if (!stored) {
    return (
      <span
        className="itemValue schedule--unset"
        data-testid={`${testId}-notset`}
        style={{ color: "var(--neg-500, #d33)", fontWeight: 600 }}
      >
        Not set
      </span>
    );
  }
  // fmtDateShort matches the ^YYYY-MM-DD PREFIX and builds Date.UTC from it, so
  // it renders both stored formats correctly by taking the calendar date and
  // discarding the time. That is by design, not luck — see fmtDate.js.
  return (
    <span className="itemValue" data-testid={`${testId}-value`}>
      {fmtDateShort(stored)}
    </span>
  );
}

export default function CampaignSchedulePanel({ campaign, campaignId, onChanged, canEdit, onNavigate }) {
  const row = campaign || {};
  const status = Number(row.campaign_status);
  const id = campaignId || row.id;
  const isFund = String(row.type || "").toUpperCase() === "FUND";
  const isLive = status === APPROVED;
  const editable = EDITABLE_STATUSES.includes(status);

  const [mode, setMode] = useState("idle"); // idle | start | end
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [busy, setBusy] = useState(false);

  /* Both inputs are datetime-local, and both prefill from the STORED value
     including its time of day.
     A date-only picker looked simpler and was wrong: it drops the time, so an
     admin who opened the panel on a campaign scheduled for 10:00 and saved
     without changing anything would silently move its launch to 00:00. Caught by
     the spec that asserts the round trip preserves 10:00. */
  const openStart = () => {
    setStartInput(toDateTimeLocalValue(row.launchDate));
    setMode("start");
  };
  const openEnd = () => {
    // Prefill the DERIVED default when nothing is stored, so the admin can see
    // and edit the value the system would otherwise apply invisibly. The display
    // row above still shows "Not set" — showing the derived value there would
    // recreate exactly the invisibility this panel exists to remove.
    const stored = toDateTimeLocalValue(row.campaing_end_date);
    setEndInput(stored || toDateTimeLocalValue(defaultEndFromLaunch(row.launchDate || row.publishedAt)));
    setMode("end");
  };

  const derivedHint =
    !row.campaing_end_date && (row.launchDate || row.publishedAt)
      ? `Leave as-is to use ${toDateInputValue(defaultEndFromLaunch(row.launchDate || row.publishedAt))} (start + 3 months), or clear it.`
      : "";

  async function saveEnd() {
    setBusy(true);
    const result = await updateCampaignSchedule(id, {
      endDate: toApiDateTime(endInput),
      currentEndDate: row.campaing_end_date,
      campaignName: row.corporation_legal_name || row.campaign_name,
      campaignStatus: status,
    });
    setBusy(false);
    if (result.success) {
      setMode("idle");
      // QA 2026-09-07 (F27): a withdraw moves the row to Upcoming; stay on the
      // Active URL and the Active panel keeps offering Pause/Close on it.
      if (result.navigateTo && onNavigate) onNavigate(result.navigateTo);
      else if (onChanged) onChanged();
    }
  }

  async function saveStart() {
    setBusy(true);
    // On a LIVE campaign, setting a start date is an unpublish — it goes through
    // the endpoint that owns that transition (and audits it as UNPUBLISH),
    // never as a side effect of a date edit.
    const result = isLive
      ? await withdrawAndReschedule(id, toApiDateTime(startInput), {
          campaignName: row.corporation_legal_name || row.campaign_name,
          campaignStatus: status,
        })
      : await updateCampaignSchedule(id, {
          launchDate: toApiDateTime(startInput),
          campaignName: row.corporation_legal_name || row.campaign_name,
          campaignStatus: status,
        });
    setBusy(false);
    if (result.success) {
      setMode("idle");
      // QA 2026-09-07 (F27): a withdraw moves the row to Upcoming; stay on the
      // Active URL and the Active panel keeps offering Pause/Close on it.
      if (result.navigateTo && onNavigate) onNavigate(result.navigateTo);
      else if (onChanged) onChanged();
    }
  }

  return (
    <div className="left" data-testid="campaign-schedule-panel">
      <h1 className="title">Campaign Schedule</h1>

      <div className="detailItem">
        <span className="itemKey">Start date:</span>
        <Value stored={row.launchDate} testId="schedule-start" />
        {editable && mode !== "start" && canEdit !== false && (
          <button
            type="button"
            className="approveButton"
            style={{ marginLeft: 12 }}
            onClick={openStart}
            data-testid="schedule-start-edit"
          >
            {isLive ? "Withdraw and reschedule" : "Change start date"}
          </button>
        )}
      </div>

      {mode === "start" && (
        <div className="detailItem" data-testid="schedule-start-form">
          <input
            type="datetime-local"
            className="field__input"
            value={startInput}
            min={`${todayDateInputValue()}T00:00`}
            onChange={e => setStartInput(e.target.value)}
            data-testid="schedule-start-input"
          />
          {/* The submit button is deliberately NOT disabled on client-side
              rules the server also enforces. Disabling it would make
              end_date_not_after_start, invalid_launch_date and
              fund_schedule_unsupported unreachable, so the requirement that the
              server's refusal be surfaced would become untestable by
              construction — the "guard that could never fail" shape. */}
          <button type="button" className="approveButton" disabled={busy} onClick={saveStart}>
            Save start date
          </button>
          <button type="button" className="approveButton" disabled={busy} onClick={() => setMode("idle")}>
            Cancel
          </button>
        </div>
      )}

      <div className="detailItem">
        <span className="itemKey">End date:</span>
        {isFund ? (
          <span className="itemValue" data-testid="schedule-end-fund">
            Not applicable — fund campaigns run continuously and are not closed by an end date.
          </span>
        ) : (
          <Value stored={row.campaing_end_date} testId="schedule-end" />
        )}
        {editable && !isFund && mode !== "end" && canEdit !== false && (
          <button
            type="button"
            className="approveButton"
            style={{ marginLeft: 12 }}
            onClick={openEnd}
            data-testid="schedule-end-edit"
          >
            {row.campaing_end_date ? "Change end date" : "Set end date"}
          </button>
        )}
      </div>

      {/* The §B.2 finding, stated on the screen where it can be acted on — and
          only where the consequence is real. A draft that never closes is not a
          problem; a LIVE campaign that never closes never reaches a
          FUNDED / FAILED_MINIMUM determination. */}
      {isLive && !isFund && !row.campaing_end_date && (
        <div className="detailItem" data-testid="schedule-never-closes">
          <span className="itemValue" style={{ color: "var(--neg-500, #d33)" }}>
            This campaign has no end date, so the closing job will never pick it up and it will never
            reach a FUNDED / FAILED_MINIMUM determination.
          </span>
        </div>
      )}

      {mode === "end" && (
        <div className="detailItem" data-testid="schedule-end-form">
          <input
            type="datetime-local"
            className="field__input"
            value={endInput}
            onChange={e => setEndInput(e.target.value)}
            data-testid="schedule-end-input"
          />
          {derivedHint && <span className="field__hint">{derivedHint}</span>}
          <button type="button" className="approveButton" disabled={busy} onClick={saveEnd}>
            Save end date
          </button>
          <button type="button" className="approveButton" disabled={busy} onClick={() => setEndInput("")}>
            Clear
          </button>
          <button type="button" className="approveButton" disabled={busy} onClick={() => setMode("idle")}>
            Cancel
          </button>
        </div>
      )}

      {!editable && (
        <div className="detailItem">
          <span className="itemValue" data-testid="schedule-readonly-reason">
            {whyReadOnly(status)}
          </span>
        </div>
      )}

      <div className="detailItem">
        <span className="field__hint">
          Dates are interpreted in the platform's business timezone (America/Toronto).
        </span>
      </div>
    </div>
  );
}
